import React, { useEffect, useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import BuildIcon from '@mui/icons-material/Build';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContrastIcon from '@mui/icons-material/Contrast';
import IosShareIcon from '@mui/icons-material/IosShare';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useWarbandStore } from '../../stores/warbandStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { useUiPreferencesStore } from '../../stores/uiPreferencesStore';
import { warbandService } from '../../services/warbandService';
import { ConfirmActionDialog } from '../shared/ConfirmActionDialog';
import { SpellSchoolChip } from '../shared/SpellSchoolChip';
import { GoogleSignInButton } from '../shared/GoogleSignInButton';
import { getWizardLevelDescription } from '../../utils/formatting';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import { GameSystemSelector } from '../shared/GameSystemSelector';
import { useGameStore } from '../../stores/gameStore';
import { getGameDefinition } from '../../game/gameDefinitions';
import { ExportWarbandModal } from '../modals/ExportWarbandModal';

export const WarbandDrawer: React.FC = () => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuWarbandId, setMenuWarbandId] = useState<string | null>(null);
  const [warbandPendingDelete, setWarbandPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [exportTarget, setExportTarget] = useState<{ id: string; name: string } | null>(null);

  const {
    ui,
    setMenuOpen,
    warbands,
    setWarbands,
    selectWarband,
    selectedWarbandId,
    openCreateWarbandModal,
    openCustomBestiaryModal,
    openCustomEquipmentModal,
    openCustomBuffsModal,
    warbandsLoaded,
  } = useWarbandStore();
  const { loadReferenceData, getSchoolNameById } = useReferenceDataStore();
  const activeGameSystem = useGameStore((state) => state.activeGameSystem);
  const setActiveGameSystem = useGameStore((state) => state.setActiveGameSystem);
  const themeMode = useUiPreferencesStore((state) => state.themeMode);
  const setThemeMode = useUiPreferencesStore((state) => state.setThemeMode);
  const game = getGameDefinition(activeGameSystem);
  const visibleWarbands = warbands.filter((warband) => (warband.gameSystem ?? 'frostgrave') === activeGameSystem);

  useEffect(() => {
    // Fetch warbands when component mounts
    warbandService
      .getAll()
      .then((res) => {
        setWarbands(res.data);
      })
      .catch((err) => {
        console.error('Failed to fetch warbands:', err);
      });
  }, [setWarbands]);

  useEffect(() => {
    void loadReferenceData();
  }, [loadReferenceData]);

  const handleWarbandSelect = (warbandId: string) => {
    selectWarband(warbandId);
    setMenuOpen(false);
  };

  const handleOpenItemMenu = (
    event: React.MouseEvent<HTMLElement>,
    warbandId: string
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuWarbandId(warbandId);
  };

  const handleCloseItemMenu = () => {
    setMenuAnchorEl(null);
    setMenuWarbandId(null);
  };

  const handleDeleteItemRequest = () => {
    if (!menuWarbandId || isDeleting) {
      return;
    }

    const warbandToDelete = warbands.find((warband) => warband.id === menuWarbandId);
    if (!warbandToDelete) {
      handleCloseItemMenu();
      return;
    }

    setWarbandPendingDelete({ id: warbandToDelete.id, name: warbandToDelete.name });
    setIsDeleteConfirmOpen(true);
    handleCloseItemMenu();
  };

  const handleDeleteWarbandConfirm = async () => {
    if (!warbandPendingDelete || isDeleting) {
      return;
    }

    setIsDeleting(true);
    try {
      await warbandService.delete(warbandPendingDelete.id);

      const updatedWarbands = warbands.filter((warband) => warband.id !== warbandPendingDelete.id);
      setWarbands(updatedWarbands);

      if (selectedWarbandId === warbandPendingDelete.id) {
        selectWarband(null);
      }

      setIsDeleteConfirmOpen(false);
      setWarbandPendingDelete(null);
    } catch (err) {
      console.error('Failed to delete warband:', err);
      window.alert(`Failed to delete ${warbandPendingDelete.name}. Please try again.`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Drawer
      anchor="left"
      open={ui.menuOpen}
      onClose={() => setMenuOpen(false)}
      sx={{
        '& .MuiDrawer-paper': {
          width: 300,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: 2,
        }}
      >
        <GameSystemSelector
          value={activeGameSystem}
          onChange={(gameSystem) => {
            setActiveGameSystem(gameSystem);
            selectWarband(null, { keepMenuOpen: true });
          }}
          fullWidth
        />
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => {
              openCreateWarbandModal();
              setMenuOpen(false);
            }}
          >
            Create {game.partyName}
          </Button>
        </Box>


        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {!warbandsLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : visibleWarbands.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              No {game.partyName.toLowerCase()}s yet. Create one to get started!
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {visibleWarbands.map((warband) => (
                <ListItem
                  key={warband.id}
                  disablePadding
                  sx={{
                    backgroundColor:
                      selectedWarbandId === warband.id ? 'rgba(255, 255, 255, 0.25)' : 'transparent',
                    borderRadius: 1,
                  }}
                >
                  <ListItemButton
                    onClick={() => handleWarbandSelect(warband.id)}
                    sx={{
                      borderLeft:
                        selectedWarbandId === warband.id
                          ? '4px solid rgba(255, 255, 255, 0.9)'
                          : 'none',
                      pl: selectedWarbandId === warband.id ? 2 : 2,
                    }}
                  >
                    <Stack sx={{ minWidth: 0, flex: 1 }}>
                      <ListItemText
                        primary={warband.name}
                        sx={{ minWidth: 0 }}
                        primaryTypographyProps={{
                          variant: 'subtitle1',
                          noWrap: true,
                          sx: {
                            fontWeight:
                              selectedWarbandId === warband.id ? 700 : 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                      {activeGameSystem === 'frostgrave' && (
                        <SpellSchoolChip
                          label={getWizardLevelDescription(warband) + ' ' + (getSchoolNameById(warband.schoolId) ?? 'Unknown School')}
                        />
                      )}
                    </Stack>

                  </ListItemButton>
                  <IconButton
                    aria-label={`more actions for ${warband.name}`}
                    onClick={(event) => handleOpenItemMenu(event, warband.id)}
                    sx={{ mr: 1 }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
          <ContrastIcon fontSize="small" />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={themeMode}
            aria-label="Theme"
            onChange={(_event, value) => {
              if (value) setThemeMode(value);
            }}
          >
            <ToggleButton value="default">Default</ToggleButton>
            <ToggleButton value="simple">Simple</ToggleButton>
            <ToggleButton value="highContrast">High Contrast</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<AutoStoriesIcon />}
            onClick={() => {
              openCustomBestiaryModal();
              setMenuOpen(false);
            }}
          >
            {'Custom Bestiary'}
          </Button>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<BuildIcon />}
            onClick={() => {
              openCustomEquipmentModal();
              setMenuOpen(false);
            }}
          >
            Custom Equipment
          </Button>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<AutoFixHighIcon />}
            onClick={() => {
              openCustomBuffsModal();
              setMenuOpen(false);
            }}
          >
            Custom Effects
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />
        <Button
          startIcon={<HelpCenterIcon />}
          href="mailto:hello@mini-fridge.app"
          target="_blank"
          rel="noopener"
          variant="outlined"
          size="small"
          sx={{ mt: 1 }}
        >
          Talk to us!
        </Button>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ pb: 1 }}>
          <GoogleSignInButton compact showLogoutButton />
        </Box>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseItemMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              const target = warbands.find((warband) => warband.id === menuWarbandId);
              if (target) setExportTarget({ id: target.id, name: target.name });
              handleCloseItemMenu();
            }}
          >
            <IosShareIcon fontSize="small" sx={{ mr: 1 }} /> Export…
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={handleDeleteItemRequest}
            disabled={isDeleting}
          >
            Delete
          </MenuItem>
        </Menu>

        <ConfirmActionDialog
          open={isDeleteConfirmOpen}
          title="Confirm Delete"
          message={`Delete ${warbandPendingDelete?.name ?? 'this warband'}? This action cannot be undone.`}
          confirmLabel="Delete"
          confirmColor="error"
          busy={isDeleting}
          onClose={() => {
            if (isDeleting) return;
            setIsDeleteConfirmOpen(false);
            setWarbandPendingDelete(null);
          }}
          onConfirm={handleDeleteWarbandConfirm}
        />
        <ExportWarbandModal
          open={exportTarget !== null}
          warbandId={exportTarget?.id ?? null}
          warbandName={exportTarget?.name ?? 'warband'}
          onClose={() => setExportTarget(null)}
        />
      </Box>
    </Drawer>
  );
};
